import { createApi } from '@reduxjs/toolkit/query/react';

import { RootState } from '@/redux/store';
import { entitlementMiddleware } from '@/redux/middleware/entitlementMiddleware';

import { baseQueryCamelized } from './helpers/baseQueryCamelized';

export enum TagTypes {
  AiConversation = 'AiConversation',
  ActiveXboardingEmployeeIds = 'ActiveXboardingEmployeeIds',
  AddOn = 'AddOn',
  AdditionalInfo = 'AdditionalInfo',
  Application = 'Application',
  ApplicationProvisioningSettings = 'ApplicationProvisioningSettings',
  ApplicationUsers = 'ApplicationUsers',
  Asset = 'Asset',
  AssetDetail = 'AssetDetail',
  AssetLog = 'AssetLog',
  AssetImage = 'AssetImage',
  BillingInfo = 'BillingInfo',
  Device = 'Device',
  DeviceMetrics = 'DeviceMetrics',
  Division = 'Division',
  DivisionEmployees = 'DivisionEmployees',
  Employee = 'Employee',
  EmployeeDirectory = 'EmployeeDirectory',
  EmployeeGroups = 'EmployeeGroups',
  EmployeeBreach = 'EmployeeBreach',
  EmployeeWithDevice = 'EmployeeWithDevice',
  EmployeeApplicationAccount = 'EmployeeApplicationAccount',
  EmployeeStatusChangeLog = 'EmployeeStatusChangeLog',
  EmployeeApplicationsAndRequests = 'EmployeeApplicationsAndRequests',
  EmployeesApplicationsAndPendingRequests = 'EmployeesApplicationsAndPendingRequests',
  GroupsAndApplicationsForEmployee = 'GroupsAndApplicationsForEmployee',
  GroupEmployees = 'GroupEmployees',
  GroupsGrantingAccessForApplication = 'GroupsGrantingAccessForApplication',
  OrganizationApplicationChampionsWithEmployees = 'OrganizationApplicationChampionsWithEmployees',
  GlobalSite = 'GlobalSite',
  Group = 'Group',
  HelpfulLinks = 'HelpfulLinks',
  Order = 'Order',
  HardwareQuote = 'HardwareQuote',
  InternalOrder = 'InternalOrder',
  InternalBillingContracts = 'InternalBillingContracts',
  InternalBillingQuotes = 'InternalBillingQuotes',
  InternalBillingProducts = 'InternalBillingProducts',
  InternalBillingCustomers = 'InternalBillingCustomers',
  InternalBillingEstimateQuote = 'InternalBillingEstimateQuote',
  InternalInvoices = 'InternalInvoices',
  Product = 'Product',
  ProductProfile = 'ProductProfile',
  ProductMarginRule = 'ProductMarginRule',
  ProductSuggestions = 'ProductSuggestions',
  HrisIntegrationIssue = 'HrisIntegrationIssue',
  HrisProvider = 'HrisProvider',
  IntakeForm = 'IntakeForm',
  IntegrationUsage = 'IntegrationUsage',
  LatestOffboardingByEmployeeId = 'LatestOffboardingByEmployeeId',
  LatestOnboardingByEmployeeId = 'LatestOnboardingByEmployeeId',
  EmployeeLeave = 'EmployeeLeave',
  Location = 'Location',
  MDMInformation = 'MDMInformation',
  MicrosoftTenant = 'MicrosoftTenant',
  MyUserData = 'MyUserData',
  Notification = 'Notification',
  Offboarding = 'Offboarding',
  Onboarding = 'Onboarding',
  Organization = 'Organization',
  OrganizationApplicationChampion = 'OrganizationApplicationChampion',
  OrganizationPackage = 'OrganizationPackage',
  OrganizationPaymentMethods = 'OrganizationPaymentMethods',
  OrganizationPaymentTerms = 'OrganizationPaymentTerms',
  OrganizationRequestTypeCategoryChampion = 'OrganizationRequestTypeCategoryChampion',
  OrganizationReseller = 'OrganizationReseller',
  PaymentMethods = 'PaymentMethods',
  RequestTypeCategoryChampion = 'RequestTypeCategoryChampion',
  Request = 'Request',
  RequestType = 'RequestType',
  RequestTypeSettings = 'RequestTypeSettings',
  Role = 'Role',
  ScheduledTask = 'ScheduledTask',
  Shipment = 'Shipment',
  Site = 'Site',
  SystemRequester = 'SystemRequester',
  User = 'User',
  ApplicationResources = 'ApplicationResources',
  EmployeeApplicationAccounts = 'EmployeeApplicationAccounts',
  OSEndOfLife = 'OSEndOfLife',
  MDMSystemWithPolicyStats = 'MDMSystemWithPolicyStats',
  SoftwareCatalogApps = 'SoftwareCatalogApps',
  OrgSoftwareApps = 'OrgSoftwareApps',
  AssetEvents = 'AssetEvents',
  GroupWithChildren = 'GroupWithChildren',
  Webhook = 'Webhook',
  WebhookDelivery = 'WebhookDelivery',
}

/**
 * The "apiSlice" itself. It should be only 1 per baseUrl.
 * The others (e.g.: userApiSlice) will be extensions of this slice.
 * The endpoints of each entity/domain will be injected in other files.
 */
export const apiSlice = createApi({
  reducerPath: 'apiSlice',
  baseQuery: entitlementMiddleware(
    baseQueryCamelized(
      {
        baseUrl: window._env_?.VITE_API_BASE_URL, // the "/v1" won't be added here to allow flexibility when new versions of endpoints start coming
        prepareHeaders: (headers, api) => {
          const loggedUser = (api.getState() as RootState).loggedUser;

          if (loggedUser?.accessToken) {
            headers.set('authorization', `Bearer ${loggedUser.accessToken}`);
          }

          // Only sent when the selected org was chosen to disambiguate a
          // shared auth0_id across multiple distinct user records (NERV-5621,
          // e.g. multi-tenant isolved) - normal org switching relies on the
          // JWT alone, same as before.
          if (
            loggedUser?.requiresOrgDisambiguationHeader &&
            loggedUser.organizationId
          ) {
            headers.set(
              'X-Disambiguate-Organization-Id',
              loggedUser.organizationId
            );
          }

          return headers;
        },
      },
      // If you want to camelCase the response data, set this to true. Note that it may break existing responses.
      { shouldCamelizeResponse: false }
    )
  ),
  endpoints: () => ({}), // Its mandatory to have at least an empty object here. The endpoints will be defined and inject in the other files.
  tagTypes: Object.keys(TagTypes),
});
